import { useEffect, useState } from "react"
import { useParams, useRouteMatch } from "react-router-dom"

import { MDB_TYPES, SELECTION_TYPES, SELECTION_TYPE_LABELS } from "@meddbriefer/scenario-data/constants"

import { useScenarioType } from "../ScenarioTypeContext"

import InterventionsEditorLayout from "./InterventionEditorLayout"

import DetailsEditor from "../DetailsEditor"
import InlineEditor from "../InlineEditor"
import AddEntity from "../AddEntity"

import { genInterventionPromptAnswerRec } from "./types"
import { getIntvsBC, getIntvBC, getPromptBC } from "../urls"
import { swapUp, swapDown } from "../utils"

const EditPrompt = () => {
    const params = useParams()
    const {interventionID, promptID} = params
    const {url} = useRouteMatch()
    const {
        scenarioType,
        intvPrompts,
        intvPromptAnswers,
        uniqueIDs,
        uniqueLabels,
    } = useScenarioType()
    const [intv, setIntv] = useState(null)
    const [prompt, setPrompt] = useState(null)
    const [promptUpdated, setPromptUpdated] = useState(false)
    const [promptAnswers, setPromptAnswers] = useState([])
    const [promptAnswersUpdated, setPromptAnswersUpdated] = useState(false)

    const updatePromptField = (entityID, fldName, newVal) => {
        setPrompt(prevState => ({ ...prevState, [fldName]: newVal }))
        setPromptUpdated(true)
    }

    const updateAnswerField = (entityID, fldName, newVal) => {
        const answer = {...promptAnswers.find(a => a.id === entityID)}
        answer[fldName] = newVal
        intvPromptAnswers.update(answer)
        setPromptAnswersUpdated(true)
    }

    const addAnswer = (entityType, newID, newLabel) => {
        intvPromptAnswers.add(genInterventionPromptAnswerRec(newID, newLabel, prompt.id))
        updatePromptField(prompt.id, "answers", prompt.answers.concat(newID))
        setPromptAnswersUpdated(true)
    }

    const deleteAnswer = (item) => {
        updatePromptField(prompt.id, "answers", prompt.answers.filter(e => e !== item.id))
        intvPromptAnswers.remove(item.id)
        setPromptAnswersUpdated(true)
    }

    const moveAnswerUp = (i) => {
        updatePromptField(prompt.id, "answers", swapUp(prompt.answers, i))
        setPromptAnswersUpdated(true)
    }

    const moveAnswerDown = (i) => {
        updatePromptField(prompt.id, "answers", swapDown(prompt.answers, i))
        setPromptAnswersUpdated(true)
    }

    useEffect(
        () => {
            if (!!interventionID && !!promptID) {
                const foundIntv = scenarioType.interventions.find(e => e.id === interventionID)
                if (foundIntv) {
                    setIntv(foundIntv)
                    const foundPrompt = scenarioType.interventionPrompts.find(e => e.id === promptID)
                    if (foundPrompt) {
                        setPrompt(foundPrompt)
                        // trigger the initial loading of prompt answers
                        setPromptAnswersUpdated(true)
                    } else {
                        alert(`prompt lookup for ${promptID} failed`)
                    }
                } else {
                    alert(`intervention lookup for ${interventionID} failed`)
                }
            }

        }, [interventionID, promptID, scenarioType.interventions, scenarioType.interventionPrompts]
    )

    useEffect(
        () => {
            if (promptAnswersUpdated) {
                // triggered on initial load, add/remove answers and re-ordering of answers
                // console.log("recalculating ")
                let tmp = []
                // get answers associated with promptID
                // console.log(prompt.answers)
                const matches = scenarioType.interventionPromptAnswers.filter(e => e.promptID === prompt.id)
                // console.log(matches)
                // sort the matching answers in the order of the ids with <prompt>.answers
                prompt.answers.forEach(ansID => {
                    // need to deal with race condition prompt.answers still has ansID of deleted answer
                    const match = matches.find(e => e.id === ansID)
                    if (match) {
                        tmp.push(match)
                    }
                })
                // console.log(tmp)
                setPromptAnswers(tmp)
                setPromptAnswersUpdated(false)
            }
        }, [prompt, promptAnswersUpdated, scenarioType.interventionPromptAnswers]
    )

    useEffect(
        () => {
            // persist local state changes to scenarioType state
            // we set promptUpdated to true when we make local changes
            if (promptUpdated) {
                intvPrompts.update(prompt)
                setPromptUpdated(false)
            }
        }, [promptUpdated, prompt, intvPrompts]
    )

    if (!prompt) {
        return (<div>loading...</div>)
    }
    return (
        <InterventionsEditorLayout
            breadcrumbs = {
                [getIntvsBC(params), getIntvBC(params, intv.label), getPromptBC(params, prompt.label)]
            }
        >
            <DetailsEditor
                entity={prompt}
                fields={[{
                    name: "id",
                    label: "ID",
                    widget: null
                },{
                    name: "label",
                    label: "Label",
                    widget: "text",
                    size: 80,
                    changeHandler: updatePromptField
                },{
                    name: "abbrLabel",
                    label: "Abbr. Label",
                    widget: "text",
                    size: 80,
                    changeHandler: updatePromptField
                },{
                    name: "feedback",
                    label: "Feedback",
                    widget: "text",
                    size: 80,
                    changeHandler: updatePromptField
                },{
                    name: "selectionType",
                    label: "Selection Type",
                    widget: "select",
                    options: [
                        [SELECTION_TYPES.ONE, SELECTION_TYPE_LABELS[SELECTION_TYPES.ONE]],
                        [SELECTION_TYPES.OOM, SELECTION_TYPE_LABELS[SELECTION_TYPES.OOM]],
                        [SELECTION_TYPES.OOM_AND, SELECTION_TYPE_LABELS[SELECTION_TYPES.OOM_AND]],
                        [SELECTION_TYPES.OOM_OR, SELECTION_TYPE_LABELS[SELECTION_TYPES.OOM_OR]],
                        [SELECTION_TYPES.NONE, SELECTION_TYPE_LABELS[SELECTION_TYPES.NONE]]
                    ],
                    changeHandler: updatePromptField
                }]}
            />

            <InlineEditor
                heading="Answers"
                records={promptAnswers}
                fields={[{
                    name: "id",
                    label: "ID",
                    colWidthPct: 40,
                    widget: null
                }, {
                    name: "label",
                    label: "Label",
                    colWidthPct: 37,
                    widget: "text",
                    size: 60,
                    changeHandler: updateAnswerField
                }, {
                    name: "abbrLabel",
                    label: "Abbr. Label",
                    colWidthPct: 23,
                    widget: "text",
                    size: 34,
                    changeHandler: updateAnswerField
                }]}
                allowChangeOrder={true}
                onMoveUp={moveAnswerUp}
                onMoveDown={moveAnswerDown}
                editLink={false}
                editBaseURL={url}
                onDelete={deleteAnswer}
            >
                <AddEntity
                    entitiesDesc={[{
                        type: MDB_TYPES.ANSWER,
                        name: "Answer",
                        labelUnique: true
                    }]}
                    addHandler={addAnswer}
                    uniqueIDs={uniqueIDs}
                    uniqueLabels={uniqueLabels}
                />
            </InlineEditor>
        </InterventionsEditorLayout>
    )
}

export default EditPrompt